/** API tokens and OIDC trust (token exchange and policies). */
import { z } from 'zod';
import { Scope, uuid } from './common.js';

/** `ApiTokenCreateRequest` (api/openapi.yaml). */
export const ApiTokenCreateRequest = z.object({
  name: z.string().min(1).max(100),
  kind: z.enum(['personal', 'service']).default('personal'),
  scopes: z.array(Scope).min(1),
  projectIds: z.array(uuid).min(1).optional(),
  expiresAt: z.string().datetime({ offset: true }).optional(),
});
export type ApiTokenCreateRequest = z.infer<typeof ApiTokenCreateRequest>;

export const OidcTokenRequest = z.object({
  idToken: z.string().min(20).max(16384),
  project: z.string().min(1).max(128),
  requestedScopes: z.array(Scope).optional(),
  ttlSeconds: z.number().int().min(300).max(21600).optional(),
});
export type OidcTokenRequest = z.infer<typeof OidcTokenRequest>;

/**
 * Bounds of a trust policy's `claimConditions`: claims per policy, globs per claim, characters per claim name and per
 * value or glob. Every exchange (`POST /auth/oidc/token`, before authentication) matches them for each candidate policy.
 */
export const OIDC_CLAIM_CONDITIONS_MAX = 20;
export const OIDC_CLAIM_GLOBS_MAX = 20;
export const OIDC_CLAIM_NAME_MAX_LENGTH = 128;
export const OIDC_CLAIM_VALUE_MAX_LENGTH = 255;

const oidcClaimValue = z.string().max(OIDC_CLAIM_VALUE_MAX_LENGTH);
/**
 * `claimConditions` of a trust policy: claim → the exact value, or a list of globs one of which must match (`*` = any
 * run of characters except a line break, the rest literal; packages/domain/src/oidc.ts `claimsMatch`).
 */
export const OidcClaimConditions = z.record(
  z.string().min(1).max(OIDC_CLAIM_NAME_MAX_LENGTH),
  z.union([oidcClaimValue, z.array(oidcClaimValue).max(OIDC_CLAIM_GLOBS_MAX)]),
).refine((conditions) => Object.keys(conditions).length <= OIDC_CLAIM_CONDITIONS_MAX, `at most ${OIDC_CLAIM_CONDITIONS_MAX} claim conditions`);

export const OidcPolicyCreateRequest = z.object({
  projectId: uuid.optional(),
  name: z.string().min(1).max(120),
  scm: z.enum(['bitbucket', 'github', 'gitlab']),
  issuer: z.string().url().max(500),
  audience: z.string().min(1).max(500),
  claimConditions: OidcClaimConditions.default({}),
  scopes: z.array(Scope).min(1),
  environmentIds: z.array(uuid).nullable().optional(),
  maxTtlSeconds: z.number().int().min(300).max(21600).default(3600),
});
export type OidcPolicyCreateRequest = z.infer<typeof OidcPolicyCreateRequest>;

/** `OidcPolicyPatch` (merge patch). */
export const OidcPolicyPatch = z.object({
  name: z.string().min(1).max(120).optional(),
  audience: z.string().min(1).max(500).optional(),
  claimConditions: OidcClaimConditions.optional(),
  scopes: z.array(Scope).min(1).optional(),
  environmentIds: z.array(uuid).nullable().optional(),
  maxTtlSeconds: z.number().int().min(300).max(21600).optional(),
  disabled: z.boolean().optional(),
}).strict();
export type OidcPolicyPatch = z.infer<typeof OidcPolicyPatch>;
